<template>
    <BaseModalFront
        :show="show"
        :title="viewMode === 'terms' ? $t('perfil.pagar_plazos', 'Pagar plazos seleccionados') : $t('perfil.metodo_pago', 'Metodo de pago')"
        :subtitle="viewMode === 'terms' ? $t('perfil.pagar_plazos_subtitulo', 'Revisa los plazos antes de confirmar el pago') : ''"
        :cancel-text="viewMode === 'terms' ? $t('perfil.cancelar', 'Cancelar') : $t('perfil.volver', 'Volver')"
        :show-cancel="viewMode === 'terms' || metodoPago === 'guardada'"
        confirm-variant="alt"
        :confirm-disabled="!canConfirm"
        :loading="loading || cargandoInfoPago || procesandoPago"
        :close-on-backdrop="!loading && !procesandoPago"
        :close-on-escape="!loading && !procesandoPago"
        size="lg"
        @close="onClose"
        @x-close="onXClose"
        @confirm="handleConfirm"
    >
        <template #confirm>
            <span v-if="loading || procesandoPago">{{ $t('perfil.realizando_pago', 'Realizando pago') }}</span>
            <span v-else-if="cargandoInfoPago">{{ $t('perfil.cargando', 'Cargando...') }}</span>
            <span v-else-if="viewMode === 'terms'">{{ $t('perfil.continuar', 'Continuar') }}</span>
            <span v-else>{{ $t('perfil.realizar_pago', 'Realizar pago') }}</span>
        </template>

        <!-- === TERMS VIEW === -->
        <div v-if="viewMode === 'terms'" class="tw:flex tw:flex-col tw:gap-5">
            <div class="tw:rounded-md tw:border tw:border-slate-200 tw:overflow-hidden">
                <div class="tw:grid tw:grid-cols-3 tw:gap-4 tw:px-4 tw:py-2 tw:bg-slate-50">
                    <span class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:text-slate-400">{{ $t('perfil.fecha_cobro', 'Fecha') }}</span>
                    <span class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:text-slate-400 tw:text-center">{{ $t('perfil.importe', 'Importe') }}</span>
                    <span class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:text-slate-400 tw:text-right">{{ $t('perfil.estado', 'Estado') }}</span>
                </div>
                <div
                    v-for="(plazo, idx) in plazos"
                    :key="plazo.id || idx"
                    class="tw:grid tw:grid-cols-3 tw:gap-4 tw:items-center tw:px-4 tw:py-3 tw:bg-white tw:border-t tw:border-slate-100"
                >
                    <span class="tw:text-sm tw:text-slate-700 tw:font-medium">
                        {{ formatearFecha(plazo.fecha) || plazo.fecha || '—' }}
                    </span>
                    <span class="tw:text-center tw:text-sm tw:font-bold tw:text-slate-900">
                        {{ formatearPrecio(plazo.importe_con_gd || plazo.importe) }}
                    </span>
                    <div class="tw:flex tw:justify-end">
                        <BadgeFront
                            :variant="estadoVariant(plazo.estado)"
                            :label="estadoI18nKey(plazo.estado) ? $t(estadoI18nKey(plazo.estado)) : (plazo.estado_string || '—')"
                            :dot="true"
                            :icon="null"
                        />
                    </div>
                </div>
            </div>

            <div class="tw:flex tw:items-center tw:justify-between tw:rounded-md tw:bg-slate-50 tw:px-4 tw:py-3 tw:border tw:border-slate-200">
                <span class="tw:text-sm tw:font-semibold tw:text-slate-600">{{ $t('perfil.total_a_pagar', 'Total a pagar') }}</span>
                <span class="tw:text-lg tw:font-extrabold tw:text-slate-900">{{ formatearPrecio(total) }}</span>
            </div>

            <div class="tw:rounded-md tw:border tw:border-slate-200 tw:bg-white tw:px-4 tw:py-4">
                <p class="tw:text-xs tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:text-slate-400 tw:mb-3">
                    {{ $t('perfil.acepta_condiciones', 'Acepta las condiciones para continuar') }}
                </p>
                <FormTerminos
                    :valor="terminosValor"
                    :pago-plazos="true"
                    :mostrar-error="mostrarErrorTerminos"
                    @update:valor="terminosValor = $event"
                />
            </div>

            <div v-if="errorMsg" class="tw:flex tw:items-center tw:gap-3 tw:rounded-md tw:border tw:border-red-200 tw:bg-red-50 tw:px-4 tw:py-3">
                <i class="material-icons tw:text-red-400 tw:text-xl tw:shrink-0">error_outline</i>
                <p class="tw:m-0 tw:text-sm tw:font-semibold tw:text-red-700">{{ errorMsg }}</p>
            </div>
        </div>

        <!-- === PAYMENT VIEW === -->
        <div v-else class="tw:flex tw:flex-col tw:gap-2">
            <div class="tw:flex tw:items-center tw:justify-between tw:rounded-md tw:bg-slate-50 tw:px-4 tw:py-3 tw:border tw:border-slate-200 tw:mb-3">
                <span class="tw:text-sm tw:font-semibold tw:text-slate-600">{{ $t('perfil.total_a_pagar', 'Total a pagar') }}</span>
                <span class="tw:text-lg tw:font-extrabold tw:text-slate-900">{{ formatearPrecio(total) }}</span>
            </div>

            <!-- Saved card option -->
            <div v-if="tarjetaGuardada" class="tw:rounded-md tw:border tw:border-slate-200 tw:bg-white tw:px-4 tw:py-4">
                <ToggleSwitch
                    :value="metodoPago === 'guardada'"
                    size="sm"
                    @input="onSelectGuardada"
                >
                    {{ $t('perfil.tarjeta_guardada', 'Tarjeta guardada') }}
                    <span class="tw:text-xs tw:text-slate-500 tw:font-normal">(····{{ tarjetaLast4 }})</span>
                </ToggleSwitch>
            </div>

            <!-- New card option -->
            <div v-if="tarjetaGuardada" class="tw:rounded-md tw:border tw:border-slate-200 tw:bg-white tw:px-4 tw:py-4">
                <ToggleSwitch
                    :value="metodoPago === 'nueva'"
                    size="sm"
                    @input="onSelectNueva"
                >
                    {{ $t('perfil.nueva_tarjeta', 'Nueva tarjeta') }}
                </ToggleSwitch>
            </div>

            <!-- Paycomet form (new card or no saved card) -->
            <div v-if="metodoPago === 'nueva' || !tarjetaGuardada" class="tw:rounded-md tw:border tw:border-slate-200 tw:bg-white tw:px-4 tw:py-4">
                <!-- 3DS Challenge iframe (solo abonos) -->
                <div v-if="supports3DS && iframeUrl" class="tw:flex tw:justify-center tw:mb-2">
                    <iframe
                        :src="iframeUrl"
                        width="325"
                        height="523"
                        sandbox="allow-top-navigation allow-scripts allow-same-origin allow-forms"
                        class="tw:border-0"
                    ></iframe>
                </div>

                <template v-else>
                    <p class="tw:text-xs tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:text-slate-400 tw:mb-3">
                        {{ $t('perfil.introducir_tarjeta', 'Introduce los datos de tu tarjeta') }}
                    </p>
                    <div v-if="jetId">
                        <PagoPaycometForm
                            ref="pagoFormRef"
                            :jet_id="jetId"
                            @payToken="supports3DS ? onPagoCompletado($event) : onPayToken($event)"
                        />
                    </div>
                    <div v-else class="tw:animate-pulse tw:space-y-3">
                        <SkeletonItem customClass="tw:w-full tw:h-11 tw:rounded-xl" />
                        <SkeletonItem customClass="tw:w-full tw:h-11 tw:rounded-xl" />
                        <div class="tw:grid tw:grid-cols-3 tw:gap-3">
                            <SkeletonItem customClass="tw:col-span-2 tw:h-11 tw:rounded-xl" />
                            <SkeletonItem customClass="tw:h-11 tw:rounded-xl" />
                        </div>
                    </div>

                    <div v-if="jetId" class="tw:mt-4 tw:pt-4 tw:border-t tw:border-slate-100">
                        <ToggleSwitch
                            :value="guardarTarjeta"
                            size="sm"
                            @input="guardarTarjeta = $event"
                        >
                            {{ $t('perfil.guardar_tarjeta', 'Guardar tarjeta para futuros pagos') }}
                        </ToggleSwitch>
                    </div>
                </template>
            </div>

            <div v-if="errorMsg" class="tw:flex tw:items-center tw:gap-3 tw:rounded-md tw:border tw:border-red-200 tw:bg-red-50 tw:px-4 tw:py-3">
                <i class="material-icons tw:text-red-400 tw:text-xl tw:shrink-0">error_outline</i>
                <p class="tw:m-0 tw:text-sm tw:font-semibold tw:text-red-700">{{ errorMsg }}</p>
            </div>
        </div>
    </BaseModalFront>
</template>

<script setup>
    import { ref, toRef } from 'vue';
    import { usePlazos } from '@/composables/front/perfil/usePlazos';
    import { usePlazosPaymentFlow } from '@/composables/front/perfil/usePlazosPaymentFlow';
    import BaseModalFront from '@/modules/front/ui/BaseModalFront.vue';
    import BadgeFront from '@/modules/front/ui/BadgeFront.vue';
    import FormTerminos from '@/modules/front/ui/FormTerminos.vue';
    import ToggleSwitch from '@/modules/front/ui/ToggleSwitch.vue';
    import PagoPaycometForm from '@/modules/front/perfil/components/CambiarDatos/PagoPaycometForm.vue';
    import { SkeletonItem } from '@/modules/front/ui/skeleton';

    const props = defineProps({
        show:        { type: Boolean, default: false },
        plazos:      { type: Array,   default: () => [] },
        total:       { type: Number,  default: 0 },
        tipoVenta:   { type: String,  default: 'plazosAbono' },
        loading:     { type: Boolean, default: false },
        error:       { type: String,  default: null },
        supports3DS: { type: Boolean, default: false },
    });

    const emit = defineEmits(['close', 'confirm', 'pago-completado']);

    const { formatearPrecio, formatearFecha, estadoVariant, estadoI18nKey } = usePlazos();

    const pagoFormRef = ref(null);

    const extractCardData = () => {
        if (!pagoFormRef.value) return null;
        const form = pagoFormRef.value.$el;
        const titular = form.querySelector('[data-paycomet="cardHolderName"]')?.value?.trim();
        const mes = form.querySelector('[data-paycomet="dateMonth"]')?.value;
        const ano = form.querySelector('[data-paycomet="dateYear"]')?.value;
        return { titular, mes, ano };
    };

    const handleConfirm = () => {
        onConfirm(extractCardData());
    };

    const {
        terminosValor, mostrarErrorTerminos, terminosAceptados,
        viewMode, metodoPago, tarjetaGuardada, tarjetaLast4,
        jetId, cargandoInfoPago, procesandoPago, guardarTarjeta,
        errorMsg, canConfirm, iframeUrl,
        onClose, onConfirm, onSelectGuardada, onSelectNueva,
        onPayToken, onPagoCompletado, resetForm,
    } = usePlazosPaymentFlow({
        show: toRef(props, 'show'),
        plazos: toRef(props, 'plazos'),
        supports3DS: props.supports3DS,
        tipoVenta: toRef(props, 'tipoVenta'),
        loading: toRef(props, 'loading'),
        emit,
        pagoFormRef,
        onConfirm: payload => emit('confirm', payload),
    });

    const onXClose = () => {
        if (props.loading || cargandoInfoPago.value || procesandoPago.value) return;
        resetForm();
        emit('close');
    };
</script>
